import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Divider, constants } from 'day8-ui';
import Overview from '../sidebar/Overview';
import SidebarPricing from '../sidebar/pricing/Pricing';
import { FaCircleInfo } from "@react-icons/all-files/fa6/FaCircleInfo";
import { BOOKING, CREW } from '../../constants';
import { getBoatWeek, isCabinBooking, isStandardBooking } from '../../helpers/booking';
import { capitalizeFirstLetter } from '../../helpers/utilities';
import { getDestinationImage } from '../../helpers/destination';
import ForeignCurrencyInfo from './ForeignCurrencyInfo';
import FreeCancellation from '../sidebar/FreeCancellation';
import SidebarPills from '../sidebar/SidebarPills';
import BookCall from '../sidebar/BookCall';
import { useYachtModal } from '../../context/YachtModalContext';
import { useToolipModal } from '../../context/TooltipModalContext';
const { CUSTOM_BOOKING_DESTINATIONS } = constants;
const renderRow = ({
  label, value, id, style
}) => (
  <div key={id} className="row" style={style}>
    <span className="col-xs-9 s-font">{label}</span>
    <span className="col-xs-3 tar s-font" style={{ float: 'right' }}>
      {value}
    </span>
  </div>
);

const Summary = ({
  booking, match, user, priceVisibilityHidden, priceVisibilityPartial
}) => {
  const { openYachtModal } = useYachtModal();
  const { openTooltipModal } = useToolipModal();
  const order = booking.mainOrder;
  const crew = {
    boys: booking.numBoys,
    girls: booking.numGirls,
    skipper: booking.externalSkipper ? 1 : 0,
    hostess: booking.needHostess ? 1 : 0
  };

  const deadlines = booking.mainOrder.orderDeadlines.map(deadline => ({
    date: deadline.dueDate,
    percentage: deadline.percent,
    amount: deadline.amount
  }));
  if (deadlines.length === 0) {
    deadlines.push({
      date: order.dueDate,
      percentage: 100
    });
  }
  const boatType = booking.boatWeek && booking.boatWeek.boat.type;
  const isCabinBookingBool = booking && isCabinBooking(booking);
  const isStandardBookingBool = isStandardBooking(booking);
  const boatWeek = getBoatWeek(booking);
  const standardYacht = booking?.standardYachtWeek?.standardYacht;
  const showYachtLink = booking && [BOOKING.OK_STATUS, BOOKING.DEFERRED_STATUS, BOOKING.PENDING_STATUS].includes(booking.status) && boatWeek;
  const standardMaxCapacity = standardYacht?.maxCapacity || CREW.MAX_GUESTS;
  const hideYachtInfo = CUSTOM_BOOKING_DESTINATIONS.includes(booking.week.destination.id);
  const userIsBooker = user && booking.booker?.id === user.id;
  const invoiceBookingId = userIsBooker && booking.status === BOOKING.OK_STATUS && booking.mainOrder ? booking.id : null;
  return (
    <div>
      <Card cardType='BORDERED'>
        <Overview
          booking={booking}
          match={match}
          boat={booking.boatWeek && booking.boatWeek.boat}
          week={booking.week}
          image={{
            url: booking.destinationImage || getDestinationImage(booking.week && booking.week.destination),
            alt: booking.week && booking.week.destination && booking.week.destination.country
          }}
          packageData={booking.package}
          flexible={false}
        />
        <SidebarPills
          className={'dui-mt-4'}
          week={booking.week}
          hideDiscountPill
          isCabin={isCabinBookingBool}
          packageId={booking.package?.id}
        />
        <Divider className='dui-my-8' />
        {!hideYachtInfo && (
          <>
            <div>
              {isStandardBookingBool && (
                <>
                  <div className='dui-flex dui-justify-between dui-items-center dui-mb-4'>
                    <p className='dui-font-normal'>Package:</p>
                    <Button buttonType="LINK_UNDERLINE" buttonSize="SMALL" rightIcon={<FaCircleInfo />} onClick={() => openYachtModal({
                      title: capitalizeFirstLetter(booking.package?.name),
                      years: `${standardYacht?.yearFrom}${standardYacht?.yearTo ? `-${standardYacht.yearTo}` : ''}`,
                      maxCapacity: standardMaxCapacity,
                      maxCapacityTooltipText: `Max. crew size: ${standardMaxCapacity}`,
                      length: `${standardYacht?.lengthFrom}${standardYacht?.lengthTo ? `-${standardYacht.lengthTo}` : ''}`,
                      hasAircon: standardYacht?.ac,
                      hullType: standardYacht?.hullType,
                      crew,
                      week: booking.week,
                      packageData: booking.package,
                      images: standardYacht?.images?.map(image => image.path)
                    })}>
                      {capitalizeFirstLetter(booking.package?.name)}
                    </Button>
                  </div>
                </>
              )}
              <div className='dui-flex dui-justify-between dui-items-center'>
                <p className='dui-font-normal'>Yacht info:</p>
                <Button buttonType="LINK_UNDERLINE" buttonSize="SMALL" rightIcon={<FaCircleInfo />} onClick={() => showYachtLink ? openYachtModal({
                  title: boatWeek.boat.type,
                  years: boatWeek.boat.year,
                  bathrooms: boatWeek.boat.heads,
                  maxCapacity: boatWeek.boat.capacity - 1,
                  maxCapacityTooltipText: `Max. crew size: ${boatWeek.boat.capacity}`,
                  length: boatWeek.boat.size,
                  hasAircon: boatWeek.boat.attributes.indexOf(1) !== -1 && boatWeek.boat.attributes.indexOf(2) !== -1,
                  images: boatWeek.boat.images,
                  hullType: boatWeek.boat.hullType,
                  suitablePackageIds: boatWeek.boat.suitablePackageIds,
                  crew,
                  week: booking.week
                }) : openTooltipModal({
                  title: 'Yacht not assigned yet!',
                  tooltipText: "Exciting times ahead! Your yacht will be assigned closer to your event date. Check back here later to see your yacht details."
                })}>
                  {showYachtLink ? boatType : 'Not assigned'}
                </Button>
              </div>
            </div>
            <Divider className='dui-my-8' />
          </>
        )}
        {!priceVisibilityHidden && !priceVisibilityPartial && (
          <SidebarPricing
            // className="margin-bottom-md"
            totalPrice={booking.mainOrder.gross / 100}
            costs={booking.refactoredOrder}
            baseCurrency={order.currency}
            destinationId={booking?.destination?.id}
            week={booking.week}
            crew={crew}
            pricePerPerson={booking.mainOrder.gross / (booking.numGirls + booking.numBoys) / 100}
          />
        )}
        <FreeCancellation
          className="margin-top-md padding-top-md border-top border-contrast-low"
          bookingId={invoiceBookingId}
          destinationId={booking.week && booking.week.destination.id}
          firstDeadline={!!deadlines.length && deadlines[0]}
          bookingDate={booking?.bookingDate}
        />
      </Card>
      {!priceVisibilityHidden && <ForeignCurrencyInfo className="dui-mt-4" baseCurrency={booking?.week?.currency} isPaymentPage />}
      {booking.status === BOOKING.PENDING_STATUS && <BookCall className="dui-mt-4" calendlyLink='https://calendly.com/yachtweek-help' />}
    </div>
  );
};

Summary.propTypes = {
  booking: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  match: PropTypes.object,
  isAutoUpgradable: PropTypes.bool,
  priceVisibilityHidden: PropTypes.bool,
  priceVisibilityPartial: PropTypes.bool
};

renderRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  style: PropTypes.object
};

export default Summary;
